import React from 'react';
import { CheckIcon, LockKeyholeIcon } from 'lucide-react';
import { ConsultantContactList } from '../components/consultants/ConsultantContactList';
import { EmployerInquiryForm } from '../components/forms/EmployerInquiryForm';
import { ProcessTimeline } from '../components/sections/ProcessTimeline';
import { ArchitecturalForm } from '../components/ui/ArchitecturalForm';
import { ButtonLink } from '../components/ui/ButtonLink';
import { FaqList } from '../components/ui/FaqList';
import { PageHero } from '../components/ui/PageHero';
import { SectionHeading } from '../components/ui/SectionHeading';
import { employerFaqs } from '../data/faqs';
import { leadershipFunctions } from '../data/opportunities';
import { usePageMeta } from '../hooks/usePageMeta';
const challenges = [{
  title: 'The best leaders are rarely applying',
  description: 'Many of the most suitable candidates are succeeding in their current roles and will only consider a move through a direct, discreet approach.'
}, {
  title: 'The brief is harder than it looks',
  description: 'Senior roles evolve with strategy. Defining what the role must achieve is often the most important — and most overlooked — step.'
}, {
  title: 'Sensitivity and confidentiality',
  description: 'Replacing an incumbent or building a team for an unannounced plan requires careful control of information.'
}, {
  title: 'The stakes of a senior appointment',
  description: 'A leadership hire shapes teams, culture, and results for years. A structured process reduces risk on both sides.'
}];
const whenToEngage = ['Appointing a chief executive or another C-suite officer', 'Adding independent or non-executive directors to the board', 'Building leadership for growth, investment, or a new market', 'Replacing a leader before the change is announced', 'Hiring for a capability that is new to the organization', 'Planning for future succession in critical roles'];
const levels = ['Board and non-executive directors', 'C-suite executives', 'Executive committee members', 'Senior functional and divisional leaders'];
const infoNeeded = ['The role, its reporting line, and why it is open now', 'Your organization’s current priorities and challenges', 'Experience you consider essential versus desirable', 'Your preferred timeline and key decision-makers', 'Any confidentiality considerations', 'A job description, if available — not required'];
export function Employers() {
  usePageMeta('For Employers', 'Partner with Aurevane Search Partners for C-suite executive search, board director recruitment, senior leadership recruitment, and confidential executive search.');
  return <>
      <PageHero label="For Employers" title="Leadership Talent Aligned With Your Business Ambitions." intro="A structured, discreet search process for organizations appointing C-suite executives, board directors, and senior leaders." visual={<ArchitecturalForm variant="stack" tone="dark" data-id="element-722" data-mp="3d6ciybqxlktg" />} data-id="element-721" data-mp="seam5lkgzonp">
        <ButtonLink to="#inquiry" size="lg" data-id="element-723" data-mp="331nonm1okqcc">
          Submit Hiring Inquiry
        </ButtonLink>
        <ButtonLink to="/expertise" variant="outline-light" size="lg" data-id="element-724" data-mp="3807cjrzu4c7c">
          Explore Our Expertise
        </ButtonLink>
      </PageHero>

      <section className="bg-porcelain py-24 lg:py-28" data-id="element-725" data-mp="3cwnz4hrdyiib">
        <div className="mx-auto max-w-7xl px-6 lg:px-10" data-id="element-726" data-mp="3hq0pawbpqxd6">
          <SectionHeading label="Executive hiring challenges" title="Why senior appointments call for a different approach" data-id="element-727" data-mp="3lr2pbfdgfdg7" />
          <ul className="mt-14 grid gap-x-12 gap-y-10 md:grid-cols-2" data-id="element-728" data-mp="3qp45nsjhwynz">
            {challenges.map(c => <li key={c.title} className="border-t border-plum/15 pt-6" data-id="element-729" data-mp="3vqbg5zd0wbyc">
                <h3 className="text-xl font-semibold text-aubergine" data-id="element-730" data-mp="4j8o75pd21n2">{c.title}</h3>
                <p className="mt-3 max-w-prose leading-relaxed text-plum/70" data-id="element-731" data-mp="21b8u6bn6nc06">{c.description}</p>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-limestone py-24 lg:py-28" data-id="element-732" data-mp="26c6bkm0dc20m">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-12 lg:px-10" data-id="element-733" data-mp="kqhawnjzk7c">
          <div className="lg:col-span-5" data-id="element-734" data-mp="3rsmakbrenrtd">
            <SectionHeading label="When to engage a search partner" title="Situations where executive search adds the most value" data-id="element-735" data-mp="3rrt99qb7tuim" />
            <ul className="mt-10 space-y-4" data-id="element-736" data-mp="4ndbv5blzj88">
              {whenToEngage.map(w => <li key={w} className="flex gap-3 leading-relaxed text-plum/80" data-id="element-737" data-mp="4lk7xxsdrd3x">
                  <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-copper" aria-hidden="true" data-id="element-738" data-mp="3d3gdoug1idon" />
                  {w}
                </li>)}
            </ul>
          </div>
          <div className="lg:col-span-6 lg:col-start-7" data-id="element-739" data-mp="3mukmtdj1b4bk">
            <h2 className="text-2xl font-semibold text-aubergine" data-id="element-740" data-mp="3i1rigqrh9y00">Leadership levels and functions covered</h2>
            <div className="mt-8 grid gap-10 sm:grid-cols-2" data-id="element-741" data-mp="2ubx76dmt4hhv">
              <div data-id="element-742" data-mp="2uacgx07izkpt">
                <h3 className="font-display text-sm font-semibold text-copper-deep" data-id="element-743" data-mp="2pe5hzrdjr9hd">Levels</h3>
                <ul className="mt-4 divide-y divide-plum/10 border-y border-plum/10" data-id="element-744" data-mp="14vtk5ynhe9j2">
                  {levels.map(l => <li key={l} className="py-3 text-plum/80" data-id="element-745" data-mp="19nbkckhok62z">
                      {l}
                    </li>)}
                </ul>
              </div>
              <div data-id="element-746" data-mp="10tggdfsaolox">
                <h3 className="font-display text-sm font-semibold text-copper-deep" data-id="element-747" data-mp="10v1c38qciaod">Functions</h3>
                <ul className="mt-4 flex flex-wrap gap-2" data-id="element-748" data-mp="1oklvttubpbyp">
                  {leadershipFunctions.map(f => <li key={f} className="rounded-full border border-plum/15 bg-porcelain/70 px-3.5 py-1.5 text-sm text-plum/80" data-id="element-749" data-mp="1sjuqr213nfvu">
                      {f}
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="on-dark bg-aubergine py-24 text-porcelain lg:py-28" data-id="element-750" data-mp="35g0gdafnky8">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-12 lg:px-10" data-id="element-751" data-mp="86c6oxi9bj6t">
          <div className="lg:col-span-7" data-id="element-752" data-mp="3k7iwz3ifu1uu">
            <LockKeyholeIcon className="h-6 w-6 text-copper-light" aria-hidden="true" data-id="element-753" data-mp="3kaolagj8szpi" />
            <h2 className="mt-6 text-3xl font-semibold leading-tight tracking-tight sm:text-4xl" data-id="element-754" data-mp="3g7sa3aw185vl">Confidential search assignments</h2>
            <p className="mt-6 max-w-prose text-lg leading-relaxed text-porcelain/75" data-id="element-755" data-mp="36h76ta5eyd6c">
              When an appointment cannot be made public, we agree a confidentiality protocol at the outset, use anonymised briefings in early conversations, and disclose information in stages as candidate interest is confirmed. Communication is limited to the stakeholders you nominate.
            </p>
            <ButtonLink to="/expertise#confidential-search" variant="ghost-light" arrow className="mt-8" data-id="element-756" data-mp="2sj90ms18xn1x">
              How confidential search works
            </ButtonLink>
          </div>
          <div className="hidden lg:col-span-4 lg:col-start-9 lg:block" data-id="element-757" data-mp="qnoyar10q4w4">
            <ArchitecturalForm variant="frame" tone="dark" data-id="element-758" data-mp="lrhouo9eyb9f" />
          </div>
        </div>
      </section>

      <section className="bg-porcelain py-24 lg:py-28" data-id="element-759" data-mp="ta7gb0s9rlkg">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-12 lg:px-10" data-id="element-760" data-mp="y90yh6ii40qy">
          <div className="lg:col-span-5" data-id="element-761" data-mp="130iz1tgm3zbe">
            <div className="lg:sticky lg:top-32" data-id="element-762" data-mp="130sp1h1nq6h3">
              <SectionHeading label="The search process" title="What to expect once we begin" intro="Seven stages, with clear communication and a considered recommendation at each." data-id="element-763" data-mp="17z3zntzs0l0d" />
            </div>
          </div>
          <div className="lg:col-span-6 lg:col-start-7" data-id="element-764" data-mp="f9wonvj4y2y8">
            <ProcessTimeline data-id="element-765" data-mp="fcsppsqa57qw" />
          </div>
        </div>
      </section>

      <section id="inquiry" className="bg-limestone py-24 lg:py-28" data-id="element-766" data-mp="k880gbszqnj3">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-12 lg:px-10" data-id="element-767" data-mp="obmpfrywqe8o">
          <div className="lg:col-span-4" data-id="element-768" data-mp="1vphictwh17ia">
            <SectionHeading label="Start a search conversation" title="Submit a hiring inquiry" data-id="element-769" data-mp="1voeu6tqj9j62" />
            <h3 className="mt-10 font-display text-base font-semibold text-aubergine" data-id="element-770" data-mp="1zoyntnbwe4ng">Information that helps us begin</h3>
            <ul className="mt-4 space-y-3" data-id="element-771" data-mp="2lpqu3389tsaq">
              {infoNeeded.map(i => <li key={i} className="flex gap-3 text-sm leading-relaxed text-plum/75" data-id="element-772" data-mp="2gutmm0qn3qzy">
                  <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-copper" aria-hidden="true" data-id="element-773" data-mp="2vjg4oa7niwuv" />
                  {i}
                </li>)}
            </ul>
            <p className="mt-8 text-sm leading-relaxed text-plum/70" data-id="element-774" data-mp="34hjn2xncbdig">Prefer to talk first? Email any of our consultants directly — their details are listed below.</p>
          </div>
          <div className="rounded-2xl bg-porcelain p-6 sm:p-10 lg:col-span-8" data-id="element-775" data-mp="3ehajj4vgo2l6">
            <EmployerInquiryForm data-id="element-776" data-mp="39ho4cpw2oc9q" />
          </div>
        </div>
      </section>

      <section className="bg-porcelain py-24 lg:py-28" data-id="element-777" data-mp="39fl47d17h2ra">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-12 lg:px-10" data-id="element-778" data-mp="1k3rezb30jjqd">
          <div className="lg:col-span-4" data-id="element-779" data-mp="1f4esirg20eob">
            <SectionHeading label="Frequently asked questions" title="Questions employers often ask" data-id="element-780" data-mp="1f5hgorlzs30j" />
          </div>
          <div className="lg:col-span-8" data-id="element-781" data-mp="3qtclknm5vj5y">
            <FaqList items={employerFaqs} idPrefix="employer-faq" data-id="element-782" data-mp="3vqcst1uqyryn" />
          </div>
        </div>
      </section>

      <section className="border-t border-plum/10 bg-porcelain py-20" data-id="element-783" data-mp="3h0dwl4mr5qlt">
        <div className="mx-auto max-w-7xl px-6 lg:px-10" data-id="element-784" data-mp="3lzqh3iq5ill8">
          <h2 className="text-2xl font-semibold text-aubergine" data-id="element-785" data-mp="3ly71lucbkeki">Contact an Executive Search Consultant</h2>
          <div className="mt-8" data-id="element-786" data-mp="382v79o22sq44">
            <ConsultantContactList data-id="element-787" data-mp="3c2lvpl44qu5h" />
          </div>
        </div>
      </section>
    </>;
}